import React, { createRef, PureComponent } from 'react'
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles'
import styles from './BlockPageContainer.styles'
import Typography from '@material-ui/core/Typography/Typography'
import { RouteComponentProps } from 'react-router'
import Block from 'Blocks/components/Block/Block'
import Toolbar from '@material-ui/core/Toolbar/Toolbar'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { blockActions, blockSelectors } from 'Blocks/redux/blockStore'
import { IGlobalState } from 'configuration/reducers'
import { bindActionCreators, Dispatch } from 'redux'
import TransactionsTable from 'Transactions/components/TransactionsTable/TransactionsTable'
import {
  BlockRecord,
  IBlockGetActionCreator,
  IBlockTransactionQueryActionCreator
} from 'Blocks/types/blocks'
import { Routes } from 'App/constants/Routes'
import PageHeader from 'common/components/PageHeader/PageHeader'
import { Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TransactionRecord } from 'Transactions/types/transactions'
import { List } from 'immutable'
import withRouteQuery from 'common/hocs/withRouteQuery/withRouteQuery'
import Paginator, { IPageQuery } from 'common/components/Pagination/Pagination'
import { Hidden } from '@material-ui/core'
import withWidth, { WithWidth } from '@material-ui/core/withWidth'
import { FormattedMessage } from 'react-intl'

interface IBlockPageInputProps
  extends RouteComponentProps<{ id: string }>,
    WithStyles {}

interface IBlockPageContainerProps
  extends IBlockPageInputProps,
    IStateProps,
    IDispatchProps {
  queryParams: IPageQuery
}

interface IBlockPageContainerState {
  variant: 'sm' | 'lg'
  rows: number
  blockState: {
    loading: boolean
    error: string
  }
  transactionState: {
    loading: boolean
    error: string
    data?: List<TransactionRecord>
  }
}

const crumbs = [Routes.Home, Routes.Blocks, Routes.Block]

class BlockPageContainer extends PureComponent<
  IBlockPageContainerProps,
  IBlockPageContainerState
> {
  static getDerivedStateFromProps({ width }: WithWidth) {
    const variant = width === 'xs' || width === 'sm' ? 'sm' : 'lg'
    return {
      rows: variant == 'sm' ? 10 : 25,
      variant
    }
  }

  transactionEl = createRef<HTMLDivElement>()
  kill$ = new Subject()
  subscriber?: Subscription
  state: IBlockPageContainerState = {
    variant: 'lg',
    rows: 25,
    blockState: {
      loading: true,
      error: ''
    },
    transactionState: {
      loading: true,
      error: '',
      data: undefined
    }
  }

  componentDidMount() {
    this.fetchTransactionData()
    this.getBlock()
  }

  componentDidUpdate(
    {
      match: {
        params: { id }
      },
      queryParams: { p }
    }: IBlockPageContainerProps,
    { variant }: IBlockPageContainerState
  ) {
    if (this.props.match.params.id !== id) {
      this.getBlock()
      this.fetchTransactionData()
    } else if (this.props.queryParams.p !== p) {
      if (this.transactionEl.current) {
        this.transactionEl.current.scrollIntoView()
      }
      this.fetchTransactionData()
    } else if (variant !== this.state.variant) {
      this.fetchTransactionData()
    }
  }

  componentWillUnmount() {
    this.kill$.next()
  }

  getBlock = () => {
    const {
      match: {
        params: { id }
      }
    } = this.props
    this.setState({ blockState: { loading: true, error: '' } })
    this.kill$.next()
    const { observable } = this.props.blockActions.get({ key: id })
    this.subscriber = observable.pipe(takeUntil(this.kill$)).subscribe(
      () => this.setState({ blockState: { loading: false, error: '' } }),
      error => this.setState({ blockState: { error, loading: false } })
    )
  }

  fetchTransactionData = () => {
    const {
      match: {
        params: { id }
      },
      queryParams: { p }
    } = this.props
    this.setState({
      transactionState: { loading: true, data: undefined, error: '' }
    })
    const { observable } = this.props.blockActions.getTransaction({
      limit: this.state.rows,
      blockNumber: parseInt(id, 10),
      p
    })

    observable.subscribe(
      ({ transactions }) =>
        this.setState({
          transactionState: { loading: false, data: transactions, error: '' }
        }),
      error =>
        this.setState({
          transactionState: { loading: false, data: undefined, error }
        })
    )
  }

  renderNavigation = () => {
    const { block, queryParams } = this.props
    const { transactionState, rows } = this.state
    const hasTransaction = transactionState.data && !!transactionState.data.size
    if (!hasTransaction || !block) {
      return null
    }
    return (
      <Paginator
        totalPages={Math.ceil(block.transactionCount / rows)}
        currentPage={parseInt(queryParams.p || '1', 10)}
      />
    )
  }

  render() {
    const { match, classes, block } = this.props
    const { blockState, transactionState, variant, rows } = this.state
    const hasTransaction = transactionState.data && !!transactionState.data.size
    const navigationComponent = this.renderNavigation()
    return (
      <div className={classes.mainContainer}>
        <PageHeader
          variant="open"
          title={match.params.id}
          breadcrumbs={crumbs}
        />
        <div className={classes.section}>
          <Block
            block={block}
            error={blockState.error}
            retry={this.getBlock}
            loading={blockState.loading}
          />
        </div>
        <div className={classes.section} ref={this.transactionEl}>
          <Toolbar className={classes.title}>
            <Typography variant="title">
              <FormattedMessage id="Blocks.containers.BlockPageContainer.transactions" />
            </Typography>
            {hasTransaction && <Hidden xsDown>{navigationComponent}</Hidden>}
          </Toolbar>
          <TransactionsTable
            variant={variant}
            error={transactionState.error}
            retry={this.fetchTransactionData}
            rows={rows}
            loading={transactionState.loading}
            transactions={transactionState.data}
          />
          {hasTransaction && (
            <Toolbar className={classes.paginationContainer}>
              {navigationComponent}
            </Toolbar>
          )}
        </div>
      </div>
    )
  }
}

interface IStateProps {
  block?: BlockRecord
}

export const mapStateToProps = (
  { blocks }: IGlobalState,
  {
    match: {
      params: { id }
    }
  }: RouteComponentProps<{ id: string }>
): IStateProps => {
  return {
    block: blockSelectors.get(blocks, id)
  }
}

interface IDispatchProps {
  blockActions: {
    get: IBlockGetActionCreator
    getTransaction: IBlockTransactionQueryActionCreator
  }
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  blockActions: {
    get: bindActionCreators(blockActions.get.create, dispatch),
    getTransaction: bindActionCreators(
      blockActions.queryBlockTransactions.create,
      dispatch
    )
  }
})

export const Unconnected = BlockPageContainer

export default compose<IBlockPageContainerProps, IBlockPageContainerProps>(
  withWidth(),
  withRouteQuery,
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles)
)(BlockPageContainer)
